<template>
  <div class="document">
    <es-loader
      v-if="loading"
      style="margin: 4.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="1rem"
      style="padding: 5rem"
    />
    <div v-else>
      <div
        class="header"
        style="display: flex"
      >
        <EsHeader>
          <template #header>
            <div class="header-title">
              Location Information Details
              ({{ initialInfo.personTypeName }})
            </div>
          </template>
        </EsHeader>
        <div class="icon-right">
          <a
            role="button"
            tabindex="0"
            @keyup.enter="hideDetailsPanel()"
            @click="hideDetailsPanel()"
          >
            <font-awesome-icon
              :icon="['far', 'angle-double-left']"
              size="2x"
              title="View Details"
            />
          </a>
        </div>
      </div>
      <div style="padding: 1rem 0">
        <div class="columns">
          <div
            class="column district-name"
            style="border-right: 1px solid #dee2e6 !important;"
          >
            {{ initialInfo.districtName }}
            <span
              class="district-code"
              style="padding-left: 0.25rem"
            >
              ({{ initialInfo.districtCode }})
            </span>
            <div class="school-name">
              {{ initialInfo.schoolName }}
              <span
                class="school-code"
                style="padding-left: 0.25rem;
                font-style: italic"
              >
                ({{ initialInfo.schoolCode }})
              </span>
            </div>
          </div>
          <div
            class="column"
            style="border-right: 1px solid #dee2e6 !important"
          >
            <div
              style="font-size: 16px;
              font-weight: 700"
            >
              {{ initialInfo.lastUpdateDate | formatDateMMDDYYYY }}
            </div>
            <div class="school-code">
              Last Updated
            </div>
          </div>
          <div class="column">
            <div
              style="font-size: 16px;
              font-weight: 700"
            >
              {{ initialInfo.personTypeName }}
            </div>
            <div class="school-code">
              Person Type
            </div>
          </div>
        </div>
      </div>
      <es-form style="margin-right: 0rem;">
        <template #es-form>
          <div
            class="content"
            style="padding: 1rem 0"
          >
            <div class="columns">
              <div class="column">
                <div
                  v-if="fieldEnabled('SCHOOL_YEAR')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SCHOOL_YEAR') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.schoolYear"
                            :aria-label="fieldLabel('SCHOOL_YEAR')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('RES_DISTRICT_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('RES_DISTRICT_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input input is-static"
                            readonly
                            type="text"
                            :value="resDistrictNameAndCode"
                            :aria-label="fieldLabel('RES_DISTRICT_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('LOCAL_STUDENT_ID')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('LOCAL_STUDENT_ID') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.localId"
                            :aria-label="fieldLabel('LOCAL_STUDENT_ID')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SOURCE_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SOURCE_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapSource(initialInfo.sourceCode)"
                            :aria-label="fieldLabel('SOURCE_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ALT_STUDENT_ID')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ALT_STUDENT_ID') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.altPersonId"
                            :aria-label="fieldLabel('ALT_STUDENT_ID')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ALT_SOURCE_SIS_CODE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ALT_SOURCE_SIS_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                           class="input is-static"
                            readonly
                            type="text"
                            :value="mapSource(initialInfo.altSourceCode)"
                            :aria-label="fieldLabel('ALT_SOURCE_SIS_CODE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ACTIVE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ACTIVE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="activeStatus"
                            :aria-label="fieldLabel('ACTIVE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SOURCE_REFRESH_DATE')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SOURCE_REFRESH_DATE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.sourceRefreshDate | formatDatePreserveZone"
                            :aria-label="fieldLabel('SOURCE_REFRESH_DATE')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('LOCATION_COMMENT')"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('LOCATION_COMMENT') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <textarea
                            class="disabled-textarea"
                            style="width: 100%; resize: none"
                            disabled
                            readonly
                            rows="3"
                            :value="initialInfo.locationComment"
                            :aria-label="fieldLabel('LOCATION_COMMENT')"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column">
                <div
                  v-if="fieldEnabled('GRADE_CODE')
                    && initialInfo.personTypeCategory === 'STUDENT'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('GRADE_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapGrades(initialInfo.gradeCode)"
                            :aria-label="fieldLabel('GRADE_CODE')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('HIRE_DATE')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('HIRE_DATE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.hireDate | formatDateMMDDYYYY"
                            :aria-label="fieldLabel('HIRE_DATE')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('SALARY')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('SALARY') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            @blur="annSalaryFocusOut"
                            @focus="annSalaryFocusIn"
                            :value="initialInfo.salary"
                            :aria-label="fieldLabel('SALARY')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('JOB_CLASS_CODE')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('JOB_CLASS_CODE') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapJobClassCode(initialInfo.jobClassCode)"
                            :aria-label="fieldLabel('JOB_CLASS_CODE')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('JOB_ASSIGNMENT_1')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('JOB_ASSIGNMENT_1') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapJobAssignments(initialInfo.jobAssignment1)"
                            :aria-label="fieldLabel('JOB_ASSIGNMENT_1')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('JOB_ASSIGNMENT_2')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('JOB_ASSIGNMENT_2') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapJobAssignments(initialInfo.jobAssignment2)"
                            :aria-label="fieldLabel('JOB_ASSIGNMENT_2')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('JOB_ASSIGNMENT_3')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('JOB_ASSIGNMENT_3') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="mapJobAssignments(initialInfo.jobAssignment3)"
                            :aria-label="fieldLabel('JOB_ASSIGNMENT_3')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="fieldEnabled('ITINERANT_FLAG')
                    && initialInfo.personTypeCategory === 'EMPLOYEE'"
                  class="row"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">
                        {{ fieldLabel('ITINERANT_FLAG') }}
                      </label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control field-label is-normal">
                          <input
                            class="input is-static"
                            readonly
                            type="text"
                            :value="initialInfo.itinerantFlag
                              ? mapItinerantFlag(initialInfo.itinerantFlag) : null"
                            :aria-label="fieldLabel('ITINERANT_FLAG')"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsSelect,
  },
  props: {
    personLocation: {
      type: Object,
      required: true,
    },
    userHasAccessToPersonLocation: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      loadingError: false,
      resDistrictNameAndCode: null,
      temporaryStorage: {},
    };
  },
  created() {
    this.onLoad();
    this.temporaryStorage = this.personLocation;
    this.initialInfo = JSON.parse(JSON.stringify(this.temporaryStorage));
    this.activeStatusOptions = [
      {
        code: false,
        description: 'Inactive',
      },
      {
        code: true,
        description: 'Active',
      },
    ];
    this.getResDistrictName();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGrades',
      'activeSources',
      'activeJobAssignments',
      'activeItinerantFlags',
      'activeTitles',
      'pidSchoolYearsDesc',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldEnabledForPersonType',
      'getFieldRequiredForPersonType',
    ]),
    activeStatus() {
      switch (this.initialInfo.active) {
        case true:
          return 'Active';
        case false:
          return 'Inactive';
        default:
          return null;
      }
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    getAll() {
      const params = {};
      params.allDistricts = true;
      return params;
    },
    personTypeAccess() {
      if (this.$store.state.user.userPersonTypes
        .some(item => item.personType.code === this.personLocation.personType)) {
        return true;
      }
      return false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
      'loadGrades',
      'loadSources',
      'loadJobAssignments',
      'loadTitles',
      'loadPidSchoolYears',
      'loadItinerantFlags',
    ]),
    annSalaryFocusIn() {
      if (this.salaryTemporary !== null) {
        // Unformat  Salary value before user starts modifying it
        this.initialInfo.salary = this.salaryTemporary.toString();
      }
    },
    annSalaryFocusOut() {
      if (this.initialInfo.salary !== null) {
        // Recalculate the  Salary after ignoring the digits and "." in user input
                this.salaryTemporary = parseFloat(this.initialInfo.salary.replace(/[^\d\.]/g, "")); // eslint-disable-line
      }
      if (Number.isNaN(this.salaryTemporary)) {
        // Ensure that value is not NaN. If so, initialize it to 'this.initialInfo.salary (NULL)'.
        // This happens if user provides a blank input
        this.salaryTemporary = this.initialInfo.salary;
      }
      if (this.salaryTemporary !== null) {
        // Format display value based on calculated  Salary
        this.initialInfo.salary = this.salaryTemporary.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.,\d+)?$)/g, '$1,');
      }
    },
    anyFieldsEnabled(fieldNames) {
      // check whether any field is enabled
      const items = fieldNames.filter(fn => this.fieldEnabled(fn));
      return (items && items.length > 0);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personLocation.personType);
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.personLocation.personType);
    },
    async getResDistrictName() {
      if (this.temporaryStorage.resDistrictCode
        && (this.temporaryStorage.resDistrictCode !== null || this.temporaryStorage.resDistrictCode !== '')) {
        let temp = null;
        temp = await axios.get(`/districts/${this.temporaryStorage.resDistrictCode}?allDistricts=${true}`);
        this.resDistrictNameAndCode = `${temp.data.location.name} (${this.initialInfo.resDistrictCode})`;
      } return this.resDistrictNameAndCode;
    },
    hideDetailsPanel() {
      this.$emit('hideDetails');
    },
    mapGrades(gradeCode) {
      if (gradeCode) {
        const foundGrade = this.activeGrades.find(g => g.code === gradeCode);
        return foundGrade ? foundGrade.description : '\u00A0';
      }
      return '\u00A0';
    },

    mapItinerantFlag(flag) {
      if (flag) {
        const foundFlag = this.activeItinerantFlags.find(f => f.code === flag);
        return foundFlag ? foundFlag.description : '\u00A0';
      }
      return '\u00A0';
    },

    mapJobClassCode(code) {
      if (code) {
        const foundJobClass = this.activeTitles.find(j => j.code === code);
        return foundJobClass ? foundJobClass.description : '\u00A0';
      }
      return '\u00A0';
    },

    mapJobAssignments(code) {
      if (code) {
        const foundJobAssignment = this.activeJobAssignments.find(j => j.code === code);
        return foundJobAssignment ? foundJobAssignment.description : '\u00A0';
      }
      return '\u00A0';
    },

    mapSource(code) {
      if (code) {
        const foundSource = this.activeSources.find(s => s.code === code);
        return foundSource ? foundSource.description : '\u00A0';
      }
      return '\u00A0';
    },

    nullifyDisabledFields() {
      const fields = [
        ['ACTIVE', 'active'],
        ['ALT_STUDENT_ID', 'altPersonId'],
        ['ALT_SOURCE_SIS_CODE', 'altSourceCode'],
        ['GRADE_CODE', 'gradeCode'],
        ['LOCAL_STUDENT_ID', 'localId'],
        ['LOCATION_COMMENT', 'locationComment'],
        ['RES_DISTRICT_CODE', 'resDistrictCode'],
        ['SOURCE_CODE', 'sourceCode'],
        ['SOURCE_REFRESH_DATE', 'sourceRefreshDate'],
      ];
      fields.forEach((e) => {
        if (!this.fieldEnabled(e[0])) {
          this.payLoad[e[1]] = null;
        }
      });
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadGrades(),
          this.loadPersonTypes(),
          this.loadSources(),
          this.loadJobAssignments(),
          this.loadTitles(),
          this.loadPidSchoolYears(),
          this.loadItinerantFlags(),
        ]);
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.border-red
  border-color: red
.control
    padding-left: 30px
.columns
    margin: 0
    display: flex
    flex-wrap: wrap
.column
    padding: 0
    flex: 1 0 auto
.district-code
    font-size: 14px
    color: #6C757D
    font-style: italic
    font-weight: 400
.district-name
    color: #212529
    font-size: 18.4px
    font-weight: 700
    text-align: left
    padding-left: 2rem
.icon-right
    padding-right: 1.5rem
    padding-top: 1.5rem
.label
    width: 200px
.school-code
    font-size: 12px
    color: #6C757D
    font-weight: 400
.school-name
    color: #212529
    font-size: 14px
    font-weight: 700
.disabled-textarea
    border: 0
    resize: none
    overflow: auto
    padding-left: 0
    padding-right: 0
    pointer-events: none
.control
    padding-left: 0 !important
</style>
